import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl-next';

import Button from '@atlaskit/button/new';
import { Box, xcss } from '@atlaskit/primitives';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import ChevronDownIcon from '@atlaskit/icon/utility/chevron-down';
import EditorAddIcon from '@atlaskit/icon/core/add';
import PlusSquareIcon from '@atlaskit/icon/core/plus-square';
import ChevronRightIcon from '@atlaskit/icon/utility/chevron-right';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import usePressTracing from '@atlaskit/react-ufo/use-press-tracing';
import { fg as platform_fg } from '@atlaskit/platform-feature-flags';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import { token } from '@atlaskit/tokens';

import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';
import type { ContentType } from '@atlassian/ai-issue-create';

import { useRightSidebarContext } from '@confluence/page-layout-context';
import {
	useIssueCreateSidePanelContext,
	useIsAIIssueCreationEnabled,
	useSetIsCreatedWithAi,
	useSetIsCreatedFromTable,
	PageMode,
} from '@confluence/issue-create-side-panel';
import { useIsSidePanelOpen } from '@confluence/side-panel-state-container';
import { fg } from '@confluence/feature-gating';

import { createMultipleJiraIssuesHandle } from './builtInButtonHandlers';
import {
	createMultipleJiraIssuesHandleModernized,
	createSingleAiJiraIssueHandle,
	createSingleJiraIssueHandle,
} from './createJiraIssueHandlers';
import type { CreateIssueRecordedProps } from './HighlightActions';

type CreateJiraIssueActionsProps = {
	activeSelection?: Selection | null;
	completeKey: string;
	container: any;
	contentId: string;
	contentType?: ContentType;
	lastModified?: string | null;
	showCreateMultipleIssuesButton: boolean;
	setRecordedClick: React.Dispatch<React.SetStateAction<CreateIssueRecordedProps | undefined>>;
	setIsWarningDialogOpen: React.Dispatch<React.SetStateAction<Boolean>>;
};

const iconWrapperStyle = xcss({
	paddingTop: 'space.025',
});

export const IssueCreationType = {
	SINGLE_ISSUE: 'SINGLE_ISSUE',
	MULTIPLE_ISSUE: 'MULTIPLE_ISSUE',
	AI_ISSUE: 'AI_ISSUE',
} as const;

type IssueCreationType = (typeof IssueCreationType)[keyof typeof IssueCreationType];

// Helper function to find the parent <table> element of a given node
const findParentTable = (node: Node): HTMLTableElement | null => {
	let currentNode: Node | null = node;
	while (currentNode) {
		if (
			currentNode.nodeType === Node.ELEMENT_NODE &&
			(currentNode as HTMLElement).tagName === 'TABLE'
		) {
			return currentNode as HTMLTableElement;
		}
		currentNode = currentNode.parentNode;
	}
	return null;
};

const isSelectionInTableWithHeader = (selection?: Selection | null): boolean => {
	if (!selection || selection.rangeCount === 0) {
		return false;
	}

	const range = selection.getRangeAt(0);
	const startContainer = range.startContainer;

	const parentTable = findParentTable(startContainer);

	if (parentTable) {
		// Check if the first row of the table is a header
		const rows = parentTable.rows;
		return (
			rows.length > 0 &&
			Array.from(rows[0].cells).every(
				(cell) =>
					cell.tagName.toLowerCase() === 'th' ||
					cell.classList.contains('ak-renderer-table-number-column'),
			)
		);
	}
	return false;
};

// Use this helper function when we have the modernized bulk create ready.
const isMultiRowTableSelection = (selection?: Selection | null): boolean => {
	if (!selection || selection.rangeCount === 0) {
		return false;
	}
	const range = selection.getRangeAt(0);
	const startContainer = range.startContainer;
	const endContainer = range.endContainer;
	// Helper function to find the parent <tr> element of a given node
	const findParentTableRow = (node: Node): HTMLTableRowElement | null => {
		while (node) {
			if ((node as HTMLElement).tagName === 'TR') {
				return node as HTMLTableRowElement;
			}
			node = node.parentNode as Node;
		}
		return null;
	};
	// Find the closest <tr> ancestor for both start and end of the selection
	const startRow = findParentTableRow(startContainer);
	const endRow = findParentTableRow(endContainer);
	if (!startRow || !endRow) {
		return false;
	}
	// If the start and end are not in the same row, it's a multi-row selection
	return startRow !== endRow;
};

const isTableSelection = (selection?: Selection | null): boolean => {
	if (!selection || selection.rangeCount === 0) {
		return false;
	}
	const range = selection.getRangeAt(0);
	const startContainer = range.startContainer;
	const endContainer = range.endContainer;
	// Check if both the start and end container are within a table
	const startInTable = findParentTable(startContainer) !== null;
	const endInTable = findParentTable(endContainer) !== null;
	return startInTable && endInTable;
};

const getIssueCreationType = ({
	isAIIssueCreationEnabled,
}: {
	isAIIssueCreationEnabled: boolean;
}) => {
	if (isAIIssueCreationEnabled) {
		return IssueCreationType.AI_ISSUE;
	} else {
		return IssueCreationType.SINGLE_ISSUE;
	}
};

const ConsolidatedCreateJiraIssueButton = ({
	completeKey,
	handleCreateIssueItemClicked,
	isAIIssueCreationEnabled,
	isSelectionMultipleRows,
}: {
	completeKey: string;
	handleCreateIssueItemClicked: (issueCreationType: IssueCreationType) => () => void;
	isAIIssueCreationEnabled: boolean;
	isSelectionMultipleRows: boolean;
}) => {
	return editorExperiment('platform_editor_controls', 'variant1') &&
		platform_fg('platform_editor_controls_view_selection_toolbar') ? (
		<DropdownItem
			testId="consolidated-create-jira-issue-button"
			data-key={completeKey}
			shouldTitleWrap={false}
			elemBefore={<PlusSquareIcon label="" />}
			onClick={handleCreateIssueItemClicked(
				isSelectionMultipleRows
					? 'MULTIPLE_ISSUE'
					: getIssueCreationType({
							isAIIssueCreationEnabled,
						}),
			)}
		>
			<FormattedMessage
				id="highlight-actions.create-jira-work-item"
				defaultMessage="Create Jira work item"
				description="Create a new Jira issue"
			/>
		</DropdownItem>
	) : (
		<Button
			id="create-jira-issue-button"
			appearance="subtle"
			iconBefore={(iconProps) => <EditorAddIcon {...iconProps} label="" />}
			data-key={completeKey}
			testId="consolidated-create-jira-issue-button"
			onClick={handleCreateIssueItemClicked(
				isSelectionMultipleRows
					? 'MULTIPLE_ISSUE'
					: getIssueCreationType({
							isAIIssueCreationEnabled,
						}),
			)}
			spacing="compact"
		>
			{fg('confluence-issue-terminology-refresh') ? (
				<FormattedMessage
					id="highlight-actions.create-jira-issue-issue-term-refresh"
					defaultMessage="Jira work item"
					description="Create a new Jira issue"
				/>
			) : (
				<FormattedMessage
					id="highlight-actions.create-jira-issue"
					defaultMessage="Jira issue"
					description="Create a new Jira issue"
				/>
			)}
		</Button>
	);
};

const IssueCreateDropdownMenu = ({
	handleCreateIssueItemClicked,
	showCreateMultipleIssuesButton,
	isAIIssueCreationEnabled,
	fireMenuToggledAnalytics,
}: {
	handleCreateIssueItemClicked: (issueCreationType: IssueCreationType) => () => void;
	showCreateMultipleIssuesButton: boolean;
	isAIIssueCreationEnabled: boolean;
	fireMenuToggledAnalytics: () => void;
}) => {
	return (
		<DropdownMenu
			testId="create-jira-issue-dropdown"
			shouldRenderToParent
			placement={
				editorExperiment('platform_editor_controls', 'variant1') &&
				platform_fg('platform_editor_controls_view_selection_toolbar')
					? 'right-start'
					: undefined
			}
			trigger={
				editorExperiment('platform_editor_controls', 'variant1') &&
				platform_fg('platform_editor_controls_view_selection_toolbar')
					? ({ triggerRef, ...props }) => (
							<div id="create-jira-issue-trigger">
								<DropdownItem
									{...props}
									ref={triggerRef}
									shouldTitleWrap={false}
									elemBefore={<PlusSquareIcon label="" />}
									elemAfter={<ChevronRightIcon color={token('color.icon.subtle')} label="" />}
								>
									<FormattedMessage
										id="highlight-actions.create-jira-work-item"
										defaultMessage="Create Jira work item"
										description="Create a new Jira issue"
									/>
								</DropdownItem>
							</div>
						)
					: ({ triggerRef, ...props }) => (
							<Button
								{...props}
								id="create-jira-issue-trigger"
								appearance="subtle"
								iconBefore={(iconProps) => <EditorAddIcon {...iconProps} label="" />}
								iconAfter={(iconProps) => (
									<Box xcss={iconWrapperStyle}>
										<ChevronDownIcon {...iconProps} label="Drop down" />
									</Box>
								)}
								ref={triggerRef}
								spacing="compact"
							>
								{fg('confluence-issue-terminology-refresh') ? (
									<FormattedMessage
										id="highlight-actions.create-jira-issue-issue-term-refresh"
										defaultMessage="Jira work item"
										description="Create a new Jira issue"
									/>
								) : (
									<FormattedMessage
										id="highlight-actions.create-jira-issue"
										defaultMessage="Jira issue"
										description="Create a new Jira issue"
									/>
								)}
							</Button>
						)
			}
			onOpenChange={fireMenuToggledAnalytics}
		>
			<DropdownItemGroup>
				<DropdownItem
					shouldTitleWrap={
						editorExperiment('platform_editor_controls', 'variant1') &&
						platform_fg('platform_editor_controls_view_selection_toolbar')
							? false
							: undefined
					}
					onClick={handleCreateIssueItemClicked(getIssueCreationType({ isAIIssueCreationEnabled }))}
				>
					{fg('confluence-issue-terminology-refresh') ? (
						<FormattedMessage
							id="highlight-actions.single-issue-issue-term-refresh"
							defaultMessage="Create single work item"
							description="Create a single Jira issue"
						/>
					) : (
						<FormattedMessage
							id="highlight-actions.single-issue"
							defaultMessage="Create single issue"
							description="Create a single Jira issue"
						/>
					)}
				</DropdownItem>
				{showCreateMultipleIssuesButton && (
					<DropdownItem
						shouldTitleWrap={
							editorExperiment('platform_editor_controls', 'variant1') &&
							platform_fg('platform_editor_controls_view_selection_toolbar')
								? false
								: undefined
						}
						onClick={handleCreateIssueItemClicked(IssueCreationType.MULTIPLE_ISSUE)}
					>
						{fg('confluence-issue-terminology-refresh') ? (
							<FormattedMessage
								id="highlight-actions.multiple-issues-issue-term-refresh"
								defaultMessage="Multiple work items"
								description="Create multiple Jira issues from a table"
							/>
						) : (
							<FormattedMessage
								id="highlight-actions.multiple-issues"
								defaultMessage="Multiple issues"
								description="Create multiple Jira issues from a table"
							/>
						)}
					</DropdownItem>
				)}
			</DropdownItemGroup>
		</DropdownMenu>
	);
};

export const CreateJiraIssueActions = ({
	activeSelection,
	completeKey,
	container,
	contentId,
	contentType,
	lastModified,
	showCreateMultipleIssuesButton,
	setRecordedClick,
	setIsWarningDialogOpen,
}: CreateJiraIssueActionsProps) => {
	const { trackAIInteractionInit } = useAIEventsInstrumentation();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const rightSidebarContext = useRightSidebarContext();
	const pressTracing = usePressTracing('highlight-actions.ai-issue-create.dropdown-item-click');
	const [, actions] = useIssueCreateSidePanelContext();
	const setIsCreatedWithAi = useSetIsCreatedWithAi();
	const setIsCreatedFromTable = useSetIsCreatedFromTable();
	const isSidePanelOpen = useIsSidePanelOpen();
	const isAIIssueCreationEnabled = useIsAIIssueCreationEnabled();
	const isSelectionMultipleRows = isMultiRowTableSelection(activeSelection);
	const isSelectionInHeaderTable = isSelectionInTableWithHeader(activeSelection);
	const isSelectionInTable = isTableSelection(activeSelection);
	const showDropdownMenu =
		isSelectionInTable && isSelectionInHeaderTable && !isSelectionMultipleRows;

	const fireCreateIssueButtonClickedAnalytics = useCallback(
		(issueCreationType: IssueCreationType, isCreatedFromTable: boolean) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'confluenceHighlightActionsMenu',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'jiraIssueLink',
					type: 'ui',
					attributes: {
						issueCreationType,
						isDropdownItem: showDropdownMenu,
						isCreatedFromTable,
						pageMode: PageMode.VIEW,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent, showDropdownMenu],
	);

	const fireMenuToggledAnalytics = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'confluenceHighlightActionsMenu',
				action: 'toggled',
				actionSubject: 'menu',
				actionSubjectId: 'createJiraIssue',
				type: 'ui',
				attributes: {
					// Hardcoded to view because the editor doesn't use a dropdown menu
					pageMode: PageMode.VIEW,
				},
			},
		}).fire();
	}, [createAnalyticsEvent]);

	const handleCreateIssueItemClicked = useCallback(
		(issueCreationType: IssueCreationType) => () => {
			if (!isSidePanelOpen) {
				switch (issueCreationType) {
					case IssueCreationType.SINGLE_ISSUE:
						fireCreateIssueButtonClickedAnalytics(
							IssueCreationType.SINGLE_ISSUE,
							isSelectionInTable,
						);
						createSingleJiraIssueHandle({
							rightSidebarContext,
							actions,
							activeSelection,
							contentId,
							contentType,
							container,
							lastModified,
							pageMode: PageMode.VIEW,
						});
						break;
					case IssueCreationType.MULTIPLE_ISSUE:
						fireCreateIssueButtonClickedAnalytics(
							IssueCreationType.MULTIPLE_ISSUE,
							isSelectionInTable,
						);
						fg('confluence_bulk_issue_creation')
							? createMultipleJiraIssuesHandleModernized({
									rightSidebarContext,
									pressTracing,
									activeSelection,
									actions,
									setIsCreatedWithAi,
									setIsCreatedFromTable,
									contentId,
									container,
									lastModified,
									pageMode: PageMode.VIEW,
								})
							: createMultipleJiraIssuesHandle(container, contentId, lastModified);
						break;
					case IssueCreationType.AI_ISSUE:
						fireCreateIssueButtonClickedAnalytics(IssueCreationType.AI_ISSUE, isSelectionInTable);
						createSingleAiJiraIssueHandle({
							rightSidebarContext,
							actions,
							activeSelection,
							contentId,
							contentType,
							container,
							fireAiAnalytics: trackAIInteractionInit,
							lastModified,
							pageMode: PageMode.VIEW,
						});
						pressTracing();
						break;
				}
			} else {
				// ai specific data
				const aiData = {
					contentType,
					fireAiAnalytics: trackAIInteractionInit,
				};
				// record temp data
				const tempData = {
					rightSidebarContext,
					actions,
					activeSelection,
					contentId,
					container,
					lastModified,
					...(issueCreationType === IssueCreationType.AI_ISSUE ? aiData : {}),
				};

				setRecordedClick(tempData);
				setIsWarningDialogOpen(true);
			}
		},
		[
			isSidePanelOpen,
			fireCreateIssueButtonClickedAnalytics,
			isSelectionInTable,
			rightSidebarContext,
			actions,
			activeSelection,
			contentId,
			contentType,
			container,
			lastModified,
			pressTracing,
			setIsCreatedWithAi,
			setIsCreatedFromTable,
			trackAIInteractionInit,
			setRecordedClick,
			setIsWarningDialogOpen,
		],
	);

	return showDropdownMenu ? (
		// Dropdown is only used for selections made in a single table row to allow user the option to create multiple issues.
		<IssueCreateDropdownMenu
			handleCreateIssueItemClicked={handleCreateIssueItemClicked}
			showCreateMultipleIssuesButton={showCreateMultipleIssuesButton}
			// We explicitly disabled AI issue creation button because we want the user to use the AI toggle in the side panel.
			isAIIssueCreationEnabled={isAIIssueCreationEnabled}
			fireMenuToggledAnalytics={fireMenuToggledAnalytics}
		/>
	) : (
		<ConsolidatedCreateJiraIssueButton
			completeKey={completeKey}
			handleCreateIssueItemClicked={handleCreateIssueItemClicked}
			isAIIssueCreationEnabled={isAIIssueCreationEnabled}
			isSelectionMultipleRows={isSelectionMultipleRows}
		/>
	);
};
